import React, { useState } from 'react';
import { Button, Grid, Tooltip } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { ProductionComponentInstanceStatus } from '@paragon/contract-library';
import { humanCase } from '@paragon/utilities';

import { setStatus } from '../../actions/setStatus';
import { getStatusesByBundleGuid } from '../../selectors/bundles';
import { useAppDispatch, useAppSelector } from '../hooks';
import { defaultStatusList } from './defaultStatusList';

interface SetStatusPropsBase {
  readonly checkedBundleGuids: readonly string[];
  readonly options?: readonly ProductionComponentInstanceStatus[];
}
type SetStatusProps =
  | (SetStatusPropsBase & {
      readonly disabled?: false;
    })
  | (SetStatusPropsBase & {
      readonly disabled: true;
      readonly disabledReason?: string;
    });

export function SetStatus(props: SetStatusProps) {
  const { checkedBundleGuids, options, disabled } = props;
  const dispatch = useAppDispatch();
  const statusesByBundleGuid = useAppSelector(getStatusesByBundleGuid);
  const anyBundlesAreUnscheduled = checkedBundleGuids
    .flatMap((bundleGuid) => statusesByBundleGuid[bundleGuid])
    .some((status) => status === 'Unscheduled');

  const [statusToUpdateTo, setStatusToUpdateTo] = useState<ProductionComponentInstanceStatus | null>(null);

  const tooltip = disabled
    ? props.disabledReason
    : checkedBundleGuids.length === 0
      ? 'No bundles selected'
      : anyBundlesAreUnscheduled
        ? 'Some bundles are "Unscheduled". Schedule them to a line first.'
        : statusToUpdateTo === null
          ? 'No new status selected'
          : 'Update status';

  return (
    <Grid container spacing={1}>
      <Grid size={8}>
        <PropertySelectField<ProductionComponentInstanceStatus>
          property="New Status"
          value={statusToUpdateTo}
          options={options ?? defaultStatusList}
          getName={humanCase}
          submit={setStatusToUpdateTo}
          margin="none"
        />
      </Grid>
      <Grid size={4}>
        <Tooltip disableInteractive title={tooltip}>
          <span>
            <Button
              onClick={async () => {
                if (statusToUpdateTo === null) return;
                await dispatch(setStatus(checkedBundleGuids, statusToUpdateTo));
                setStatusToUpdateTo(null);
              }}
              variant="outlined"
              disabled={
                disabled || checkedBundleGuids.length === 0 || statusToUpdateTo === null || anyBundlesAreUnscheduled
              }
            >
              Set status
            </Button>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  );
}
